/**
 * Cloud Function Helper
 * Simplifies calling domain-based cloud functions with consistent error handling
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type { ParticipationReport } from '../../../common/src/types/participation';

/**
 * Parses cloud function errors into a user-friendly format
 */
const parseCloudFunctionError = (error: any) => {
  if (error.details) {
    try {
      const parsed = JSON.parse(error.details);
      return parsed.error || parsed;
    } catch {
      return {
        message: error.message || 'Cloud function error',
        details: error.details,
        code: error.code,
      };
    }
  }
  return {
    message: error.message || 'Unknown error occurred',
    code: error.code || 'UNKNOWN',
  };
};

/**
 * Calls a domain cloud function with the specified parameters
 *
 * @param functionName - The domain function name (e.g., 'manageAnalysis', 'managePayments')
 * @param requestParams - The request parameters including 'function' key for routing
 *
 * @example
 * // Analyze a shot
 * cloudFunctionManageRequest('manageAnalysis', {
 *   function: 'analyzeShotWithGPT',
 *   videoUrl: '...',
 *   shotData: {...}
 * })
 *
 * @example
 * // Create a checkout session
 * cloudFunctionManageRequest('managePayments', {
 *   function: 'createCheckoutSession',
 *   planType: 'monthly',
 *   successUrl: '...',
 *   cancelUrl: '...'
 * })
 */
export const cloudFunctionManageRequest = async (
  functionName: string,
  requestParams: Record<string, any>
): Promise<any> => {
  try {
    const cloudFunction = httpsCallable(functions, functionName);
    const result = await cloudFunction(requestParams);
    return result.data;
  } catch (error: any) {
    const parsedError = parseCloudFunctionError(error);
    throw parsedError;
  }
};

// ===== ANALYSIS DOMAIN HELPERS =====

/**
 * Analyzes a shot using GPT-4 Vision
 */
export const analyzeShotWithGPT = async (shotData: any) => {
  return cloudFunctionManageRequest('manageAnalysis', {
    function: 'analyzeShotWithGPT',
    ...shotData,
  });
};

// ===== PAYMENT DOMAIN HELPERS =====

// Individual subscription wrappers removed (BILLING_SPEC §7) — their
// managePayments actions no longer exist server-side. The organization
// billing wrappers below stay: standalone orgs keep self-serve card payment.

/**
 * Submits a contact sales inquiry
 */
export const submitContactSales = async (params: {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  organizationName: string;
  organizationType?: string;
  numberOfUsers?: number;
  message?: string;
}) => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'submitContactSales',
    ...params,
  });
};

/**
 * Provisions an enterprise organization (super admin only)
 */
export const provisionEnterpriseOrganization = async (params: {
  inquiryId: string;
  organization: any;
  admin: any;
  pricing: any;
}) => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'provisionEnterpriseOrganization',
    ...params,
  });
};

/**
 * Creates a Stripe billing portal session for an organization
 * Returns a URL to redirect the user to the Stripe Customer Portal
 */
export const createOrgBillingPortalSession = async (params: {
  organizationId: string;
  clientKey: string;
  returnUrl?: string;
}): Promise<{ success: boolean; url: string }> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'createOrgBillingPortalSession',
    ...params,
  });
};

// ===== ORGANIZATION BILLING HELPERS =====

/**
 * Get organization billing info including subscription and payment method
 */
export const getOrgBillingInfo = async (params: {
  organizationId: string;
  clientKey: string;
}): Promise<{
  success: boolean;
  billing: any;
  paymentMethod: {
    id: string;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
  subscription: {
    id: string;
    status: string;
    currentPeriodEnd: Date;
    cancelAtPeriodEnd: boolean;
    planType: string;
  } | null;
}> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'getOrgBillingInfo',
    ...params,
  });
};

/**
 * Get organization invoices
 */
export const getOrgInvoices = async (params: {
  organizationId: string;
  clientKey: string;
}): Promise<{
  success: boolean;
  invoices: Array<{
    id: string;
    number: string;
    date: number;
    amount: number;
    currency: string;
    status: string;
    invoicePdf: string;
    hostedUrl: string;
  }>;
}> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'getOrgInvoices',
    ...params,
  });
};

/**
 * Create organization checkout session (card payment)
 */
export const createOrgCheckoutSession = async (params: {
  organizationId: string;
  clientKey: string;
  planType: 'monthly' | 'yearly';
  successUrl?: string;
  cancelUrl?: string;
}): Promise<{ success: boolean; sessionId: string; sessionUrl: string }> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'createOrgCheckoutSession',
    ...params,
  });
};

/**
 * Create organization invoice (invoice billing)
 */
export const createOrgInvoice = async (params: {
  organizationId: string;
  clientKey: string;
  planType: 'monthly' | 'yearly';
  billingEmail?: string;
}): Promise<{
  success: boolean;
  invoiceId: string;
  invoiceUrl: string;
  invoicePdf: string;
}> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'createOrgInvoice',
    ...params,
  });
};

/**
 * Create setup intent for org payment method update
 */
export const createOrgPaymentMethodSetupIntent = async (params: {
  organizationId: string;
  clientKey: string;
}): Promise<{ success: boolean; clientSecret: string }> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'createOrgPaymentMethodSetupIntent',
    ...params,
  });
};

/**
 * Update organization payment method
 */
export const updateOrgPaymentMethod = async (params: {
  organizationId: string;
  clientKey: string;
  paymentMethodId: string;
}): Promise<{ success: boolean }> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'updateOrgPaymentMethod',
    ...params,
  });
};

/**
 * Cancel organization subscription
 */
export const cancelOrgSubscription = async (params: {
  organizationId: string;
  clientKey: string;
  cancelImmediately?: boolean;
}): Promise<{ success: boolean; subscription: any }> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'cancelOrgSubscription',
    ...params,
  });
};

/**
 * Reactivate organization subscription
 */
export const reactivateOrgSubscription = async (params: {
  organizationId: string;
  clientKey: string;
}): Promise<{ success: boolean; subscription: any }> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'reactivateOrgSubscription',
    ...params,
  });
};

// ===== ACCESS / LINKAGE HELPERS =====

export const getOrgAccessRequests = async (params: {
  organizationId: string;
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
}): Promise<{ success: boolean; requests: any[] }> => {
  const callable = httpsCallable(functions, 'getOrgAccessRequests');
  const result = await callable(params);
  return result.data as { success: boolean; requests: any[] };
};

export const getTeamAccessRequests = async (params: {
  organizationId: string;
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
}): Promise<{ success: boolean; requests: any[] }> => {
  const callable = httpsCallable(functions, 'getTeamAccessRequests');
  const result = await callable(params);
  return result.data as { success: boolean; requests: any[] };
};

export const getManagedTeamAccessRequests = async (params?: {
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
}): Promise<{ success: boolean; requests: any[] }> => {
  const callable = httpsCallable(functions, 'getManagedTeamAccessRequests');
  const result = await callable(params || {});
  return result.data as { success: boolean; requests: any[] };
};

export const approveOrgAccessRequest = async (params: { requestId: string }): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'approveOrgAccessRequest');
  const result = await callable(params);
  return result.data as { success: boolean };
};

export const rejectOrgAccessRequest = async (params: { requestId: string; reason?: string }): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'rejectOrgAccessRequest');
  const result = await callable(params);
  return result.data as { success: boolean };
};

export const approveTeamAccessRequest = async (params: { requestId: string }): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'approveTeamAccessRequest');
  const result = await callable(params);
  return result.data as { success: boolean };
};

export const rejectTeamAccessRequest = async (params: { requestId: string; reason?: string }): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'rejectTeamAccessRequest');
  const result = await callable(params);
  return result.data as { success: boolean };
};

export const approveOrganizationInvitation = async (params: {
  invitationId: string;
  userId?: string;
}): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'approveOrganizationInvitation');
  const result = await callable(params);
  return result.data as { success: boolean };
};

export const rejectOrganizationInvitation = async (params: {
  invitationId: string;
  reason?: string;
}): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'rejectOrganizationInvitation');
  const result = await callable(params);
  return result.data as { success: boolean };
};

export const cancelOrganizationInvitation = async (params: {
  invitationId: string;
}): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'cancelOrganizationInvitation');
  const result = await callable(params);
  return result.data as { success: boolean };
};

export const submitOrgClaimRequest = async (params: {
  requestType: 'org_admin_claim' | 'org_create';
  existingOrgId?: string;
  organizationName?: string;
  organizationType?: string;
  notes?: string;
  idempotencyKey?: string;
}): Promise<{ success: boolean; requestId: string; status: string; existing?: boolean }> => {
  const callable = httpsCallable(functions, 'submitOrgClaimRequest');
  const result = await callable(params);
  return result.data as { success: boolean; requestId: string; status: string; existing?: boolean };
};

export const getOrgClaimRequests = async (params?: {
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
}): Promise<{ success: boolean; requests: any[] }> => {
  const callable = httpsCallable(functions, 'getOrgClaimRequests');
  const result = await callable(params || {});
  return result.data as { success: boolean; requests: any[] };
};

export const approveOrgClaimRequest = async (params: { requestId: string }): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'approveOrgClaimRequest');
  const result = await callable(params);
  return result.data as { success: boolean };
};

export const rejectOrgClaimRequest = async (params: { requestId: string; reason?: string }): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'rejectOrgClaimRequest');
  const result = await callable(params);
  return result.data as { success: boolean };
};

// ===== IN-APP BILLING HELPERS =====

/**
 * Create setup intent for saving card (for orgs)
 */
export const createOrgSetupIntent = async (params: {
  organizationId: string;
  clientKey: string;
}): Promise<{ success: boolean; clientSecret: string; customerId: string }> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'createOrgSetupIntent',
    ...params,
  });
};

/**
 * Create organization subscription with custom pricing
 * planType: 'monthly' | 'yearly' | '6month'
 * customPriceCents: price in cents (required)
 */
export const createOrgSubscription = async (params: {
  organizationId: string;
  clientKey: string;
  paymentMethodId: string;
  planType: 'monthly' | 'yearly' | '6month';
  customPriceCents: number;
}): Promise<{
  success: boolean;
  subscriptionId: string;
  status: string;
  currentPeriodEnd: Date;
}> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'createOrgSubscription',
    ...params,
  });
};

/**
 * Pay organization invoice with card in-app
 */
export const payOrgInvoiceWithCard = async (params: {
  organizationId: string;
  clientKey: string;
  invoiceId: string;
  paymentMethodId?: string;
}): Promise<{
  success: boolean;
  status: string;
  invoiceUrl?: string;
  invoicePdf?: string;
}> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'payOrgInvoiceWithCard',
    ...params,
  });
};

/**
 * Get invoice payment intent for in-app card payment
 */
export const getOrgInvoicePaymentIntent = async (params: {
  organizationId: string;
  clientKey: string;
  invoiceId: string;
}): Promise<{
  success: boolean;
  clientSecret?: string;
  amount?: number;
  currency?: string;
  status?: string;
}> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'getOrgInvoicePaymentIntent',
    ...params,
  });
};

/**
 * Get detailed subscription info including next payment
 */
export const getOrgSubscriptionDetails = async (params: {
  organizationId: string;
  clientKey: string;
}): Promise<{
  success: boolean;
  billing: any;
  subscription: {
    id: string;
    status: string;
    currentPeriodStart: Date;
    currentPeriodEnd: Date;
    cancelAt: Date | null;
    cancelAtPeriodEnd: boolean;
    contractType: string;
  } | null;
  paymentMethod: {
    id: string;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
  upcomingInvoice: {
    amount: number;
    currency: string;
    dueDate: Date;
  } | null;
}> => {
  return cloudFunctionManageRequest('managePayments', {
    function: 'getOrgSubscriptionDetails',
    ...params,
  });
};

// ===== USER DOMAIN HELPERS =====

/**
 * Start mirroring a user (admin feature)
 */
export const startMirroringUser = async (params: {
  targetUserId: string;
  targetUserName: string;
}) => {
  return cloudFunctionManageRequest('manageUser', {
    function: 'startMirroringUser',
    ...params,
  });
};

/**
 * Stop mirroring a user (admin feature)
 */
export const stopMirroringUser = async () => {
  return cloudFunctionManageRequest('manageUser', {
    function: 'stopMirroringUser',
  });
};

// ===== AUTH DOMAIN HELPERS =====

/**
 * Refresh user's custom claims
 * This forces the Cloud Function to recalculate and update the user's auth claims
 * Useful after changes that affect permissions (e.g., linking children, role changes)
 */
export const refreshUserClaims = async (userId?: string) => {
  const refreshClaims = httpsCallable(functions, 'refreshUserClaims');
  const result = await refreshClaims({ userId });
  return result.data;
};

// ===== DISTRICT SSO =====

export type SignInMethodResolution = {
  method: 'sso' | 'password';
  provider_id?: string;
  provider_type?: 'google' | 'saml' | 'oidc' | 'classlink' | 'clever';
  district_id?: string;
  district_name?: string;
  allow_password_fallback?: boolean;
  /** Clever districts (CLEVER_SPEC §3): where to send the browser; absent when Clever is not configured. */
  clever_authorize_url?: string;
};

/* ───────────── Clever (CLEVER_SPEC) ───────────── */

export interface CleverExchangeResult {
  token: string;
  uid: string;
  district_id: string;
  created: boolean;
  rostered: boolean;
}

/** Turn the code Clever sent to /auth/clever into a Firebase custom token. */
export const exchangeCleverCode = async (code: string): Promise<CleverExchangeResult> => {
  const fn = httpsCallable(functions, 'exchangeCleverCode');
  const result = await fn({ code });
  return result.data as CleverExchangeResult;
};

export interface CleverAuthorization {
  clever_district_id: string;
  clever_district_name: string | null;
  scopes: string[];
  authorized_at: string | null;
  district_id: string | null;
}

/** Super admin: districts that authorized the app in Clever, mapped or not. */
export const listCleverAuthorizations = async (): Promise<{ configured: boolean; authorizations: CleverAuthorization[] }> => {
  const fn = httpsCallable(functions, 'listCleverAuthorizations');
  const result = await fn({});
  return result.data as { configured: boolean; authorizations: CleverAuthorization[] };
};

/**
 * Home-realm discovery: does this email belong to a district that signs in
 * through its own identity provider?
 *
 * Unknown domains come back as `password` with no district detail — that is a
 * deliberate non-answer, not a failure. Falls back to password sign-in if the
 * lookup itself fails so an outage can't lock everyone out.
 */
export const resolveSignInMethod = async (
  email: string
): Promise<SignInMethodResolution> => {
  try {
    const resolve = httpsCallable(functions, 'resolveSignInMethod');
    const result = await resolve({ email });
    return (result.data as SignInMethodResolution) || { method: 'password' };
  } catch {
    return { method: 'password' };
  }
};

// ===== DISTRICT ROSTERING =====

export interface RosterSyncResult {
  success: boolean;
  sync_id: string;
  status: 'success' | 'partial' | 'failed' | 'preview';
  users_seen: number;
  users_created: number;
  users_updated: number;
  users_deactivated: number;
  users_reactivated: number;
  aborted_reason: string | null;
  error_count: number;
  file_outcomes?: import('../../../common/src/types/district').FileParseOutcome[];
  /** Set on dry runs (ROSTERING_COMPLETION_SPEC G1): nothing was applied. */
  dry_run?: boolean;
  preview?: RosterPreviewReport;
  /** Present when the delivery carried a team roster file (TEAM_SOURCING_SPEC). */
  teams?: import('../../../common/src/types/district').TeamImportReport;
  /** Present when the delivery carried a grade file (AUTOMATION_SPEC A11). */
  academic?: import('../../../common/src/types/district').AcademicImportReport;
}

/** The report as it arrives over the wire (Dates serialized). */
export type RosterPreviewReport = Omit<
  import('../../../common/src/types/district').RosterPreviewReport,
  'created_at' | 'expires_at'
> & { created_at: unknown; expires_at: unknown };

export interface RosterRunOptions {
  /** Validate + diff, zero roster writes; the result carries `preview`. */
  dryRun?: boolean;
}

/**
 * Ingest a roster file (CSV or OneRoster ZIP) previously uploaded to
 * roster_uploads/{districtId}/… — or preview what ingesting it would do.
 */
export const processRosterUpload = async (
  districtId: string,
  storagePath: string,
  options: RosterRunOptions = {}
): Promise<RosterSyncResult> => {
  const fn = httpsCallable(functions, 'processRosterUpload');
  const result = await fn({ districtId, storagePath, dry_run: options.dryRun === true });
  return result.data as RosterSyncResult;
};

export interface DirectoryEntry {
  sourced_id: string;
  given_name: string;
  family_name: string;
  grade: string | null;
  role: string;
  org_sourced_ids: string[];
  linked: boolean;
  linked_uid: string | null;
  /** Coaching role granted by a district admin (staff rows only). */
  app_role?: 'coach' | 'team_manager' | null;
  /** Sports the grant names, as sport keys (TEAM_SOURCING_SPEC §3). */
  coach_sports?: string[];
}

export interface StaffRoleChange {
  sourced_id: string;
  display_name: string;
  linked_uid: string | null;
  from: string | null;
  to: string | null;
  audit_id: string;
  coach_sports?: string[];
}

export interface SchoolOrganizationCreated {
  organization_id: string;
  name: string;
  sourced_id: string;
  audit_id: string;
}

/** Set up a synced school in the app: creates its district-linked Organization (DISTRICT_ROLE_SPEC §4). */
export const createOrganizationForSchool = async (
  districtId: string,
  sourcedId: string
): Promise<SchoolOrganizationCreated> => {
  const fn = httpsCallable(functions, 'createOrganizationForSchool');
  const result = await fn({ districtId, sourcedId });
  return result.data as SchoolOrganizationCreated;
};

/**
 * Staff promotion (DISTRICT_ROLE_SPEC §4): grant coach / team_manager to a
 * rostered staff member, or pass null to revoke. Audited server-side.
 *
 * `sports` (sport keys, TEAM_SOURCING_SPEC §3) is what the season stand-up
 * assigns teams by. Omit it to leave the row's list untouched; pass [] to clear.
 */
export const setDistrictStaffRole = async (
  districtId: string,
  sourcedId: string,
  appRole: 'coach' | 'team_manager' | null,
  sports?: string[]
): Promise<StaffRoleChange> => {
  const fn = httpsCallable(functions, 'setDistrictStaffRole');
  const result = await fn({ districtId, sourcedId, appRole, ...(sports ? { sports } : {}) });
  return result.data as StaffRoleChange;
};

/** Staff-only search over the district's synced roster directory. */
export const searchDistrictDirectory = async (
  districtId: string,
  query: string,
  limit = 25,
  filters?: { roleFilter?: string; schoolFilter?: string }
): Promise<DirectoryEntry[]> => {
  const fn = httpsCallable(functions, 'searchDistrictDirectory');
  const result = await fn({ districtId, query, limit, ...(filters || {}) });
  return (result.data as { results: DirectoryEntry[] })?.results || [];
};

/**
 * District-wide teams overview (DISTRICT_ROLE_SPEC §3): per-school teams with
 * coach names and athlete counts, server-computed — district admins never
 * read raw team or profile docs.
 */
export const getDistrictTeamsSummary = async (
  districtId: string
): Promise<import('../../../common/src/types/district').DistrictTeamsSummary> => {
  const fn = httpsCallable(functions, 'getDistrictTeamsSummary');
  const result = await fn({ districtId });
  return result.data as import('../../../common/src/types/district').DistrictTeamsSummary;
};

/**
 * Season stand-up (TEAM_SOURCING_SPEC §3, phase 2): pre-create every linked
 * school's teams for a season from the district's sport catalog. `dryRun`
 * previews; the run itself is idempotent — an existing team reports `exists`.
 */
export const scaffoldSeasonTeams = async (args: {
  districtId: string;
  season: string;
  sports: Array<{ sport_label: string; levels: string[] }>;
  schools: 'all' | string[];
  dryRun?: boolean;
}): Promise<import('../../../common/src/types/district').SeasonScaffoldReport> => {
  const fn = httpsCallable(functions, 'scaffoldSeasonTeams');
  const result = await fn(args);
  return result.data as import('../../../common/src/types/district').SeasonScaffoldReport;
};

export interface AddRosterMembersResult {
  success: boolean;
  added: number;    // Students with accounts, added to the team immediately
  pending: number;  // Not signed in yet; join automatically on first sign-in
  skipped: string[];
}

/** Add students from the district directory to a team (SSO_SPEC §7.3). */
export const addRosterMembersToTeam = async (
  districtId: string,
  teamId: string,
  sourcedIds: string[]
): Promise<AddRosterMembersResult> => {
  const fn = httpsCallable(functions, 'addRosterMembersToTeam');
  const result = await fn({ districtId, teamId, sourcedIds });
  return result.data as AddRosterMembersResult;
};

// ===== SFTP ROSTER DELIVERY (SFTP_SPEC) =====

export interface SftpRemoteFile {
  name: string;
  size: number;
  modified_at: number; // epoch ms
  assessment: 'would_ingest' | 'already_seen' | 'too_large' | 'pattern_miss' | 'bad_name' | 'over_cap' | 'unknown';
}

export interface SftpTestResult {
  ok: boolean;
  fingerprint: string;
  pinned: boolean;
  remote_path: string;
  would_ingest_count: number;
  files: SftpRemoteFile[];
}

/**
 * Test the district's pull-mode SFTP connection: verifies the pinned host
 * key (or reports the fingerprint for confirmation), lists the drop folder.
 * Pass pinFingerprint (a value a previous call returned) to persist the pin.
 */
export const testSftpConnection = async (
  districtId: string,
  pinFingerprint?: string
): Promise<SftpTestResult> => {
  const fn = httpsCallable(functions, 'testSftpConnection');
  const result = await fn({ districtId, ...(pinFingerprint ? { pinFingerprint } : {}) });
  return result.data as SftpTestResult;
};

export interface SftpSyncSummary {
  files_found: number;
  files_ingested: number;
  files_failed: number;
  files_skipped: number;
  sync_ids: string[];
  errors: string[];
  dry_run?: boolean;
  preview?: RosterPreviewReport;
}

/**
 * Run the district's SFTP pull sync immediately ("Sync now"), or preview the
 * files the next run would ingest without touching the roster or the ledger.
 */
export const runSftpRosterSync = async (
  districtId: string,
  options: RosterRunOptions = {}
): Promise<SftpSyncSummary> => {
  const fn = httpsCallable(functions, 'runSftpRosterSync');
  const result = await fn({ districtId, dry_run: options.dryRun === true });
  return result.data as SftpSyncSummary;
};

/** Same summary shape as SFTP sync (RosterSyncResult-adjacent). */
export interface OneRosterSyncResult {
  success: boolean;
  sync_id: string;
  status: string;
  users_seen: number;
  users_created: number;
  users_updated: number;
  users_deactivated: number;
  /** Present when the run pulled grades (ACADEMIC_ELIGIBILITY_SPEC §3). */
  academic?: import('../../../common/src/types/district').AcademicImportReport;
  academic_pull?: import('../../../common/src/types/district').AcademicPullReport;
  academic_error?: string;
  aborted_reason?: string | null;
  error_count: number;
  dry_run?: boolean;
  preview?: RosterPreviewReport;
}

export interface OneRosterRunOptions extends RosterRunOptions {
  /**
   * `'results'` pulls grades and nothing else (ACADEMIC_ELIGIBILITY_SPEC
   * §3.4) — no people, no classes — so checking a line-item pattern does not
   * wait on a district-wide reconcile.
   */
  only?: 'results';
}

/** Run a OneRoster API district's sync immediately ("Sync now"), or preview the pull. */
export const runOneRosterSync = async (
  districtId: string,
  options: OneRosterRunOptions = {}
): Promise<OneRosterSyncResult> => {
  const fn = httpsCallable(functions, 'runOneRosterSync');
  const result = await fn({
    districtId,
    dry_run: options.dryRun === true,
    ...(options.only ? { only: options.only } : {}),
  });
  return result.data as OneRosterSyncResult;
};

/**
 * One platform pull (TEAM_SOURCING_SPEC §5 phase 4). A pull is a team-only
 * delivery: no people are created, so there are no user counts — the report is
 * the same team report the office's file produces.
 */
export interface TeamSourceSyncResult {
  success: boolean;
  sync_id: string;
  status: 'success' | 'partial' | 'failed' | 'preview';
  dry_run: boolean;
  vendor: string;
  rows: number;
  /** Vendor school ids this pull saw — what the school-map editor is keyed by. */
  schools_seen: string[];
  unmapped_schools: string[];
  calls: number;
  vendor_updated_at: string | null;
  teams: import('../../../common/src/types/district').TeamImportReport | null;
  preview?: RosterPreviewReport;
  errors: string[];
}

/** Pull the platform's roster now, or preview what pulling it would do. */
export const runTeamSourceSync = async (
  districtId: string,
  options: RosterRunOptions = {}
): Promise<TeamSourceSyncResult> => {
  const fn = httpsCallable(functions, 'runTeamSourceSync');
  const result = await fn({ districtId, dry_run: options.dryRun === true });
  return result.data as TeamSourceSyncResult;
};

/** Clever Secure Sync (CLEVER_SPEC §4): same result shape as OneRoster, always a full pull. */
export const runCleverSync = async (
  districtId: string,
  options: RosterRunOptions = {}
): Promise<OneRosterSyncResult> => {
  const fn = httpsCallable(functions, 'runCleverSync');
  const result = await fn({ districtId, dry_run: options.dryRun === true });
  return result.data as OneRosterSyncResult;
};

/**
 * District-admin-editable integration settings (DISTRICT_CONSOLE_SPEC D3):
 * pause/resume the sync, tune its cadence (hours between runs, 1–168), set the
 * class-period team rule (TEAM_SOURCING_SPEC §3 "Classes"), turn the platform
 * pull on with its school map (§5 phase 4), set the academic eligibility
 * policy (AUTOMATION_SPEC A11), and choose when the morning brief roll-up
 * arrives (DEPARTMENT_BRIEF_SPEC §6).
 */
export const updateDistrictIntegration = async (
  districtId: string,
  updates: {
    sync_enabled?: boolean;
    sync_schedule?: number | null;
    /**
     * Teams from class periods (TEAM_SOURCING_SPEC §3 "Classes"); null clears
     * the rule. Validated server-side before it is stored.
     */
    class_teams?: import('../../../common/src/types/district').ClassTeamRule | null;
    /**
     * The district's half of the platform config (TEAM_SOURCING_SPEC §5
     * phase 4): the switch and the school map. The vendor and its credentials
     * are the operator's and are refused here.
     */
    team_source?: { enabled: boolean; school_map: Record<string, string> };
    /** Academic eligibility (AUTOMATION_SPEC A11); null clears the policy. */
    academic_policy?: AcademicPolicyInput | null;
    /**
     * Grades from OneRoster `/results` (ACADEMIC_ELIGIBILITY_SPEC §3.2); null
     * clears the settings. Validated server-side — the pattern has to compile.
     */
    oneroster_results?: OneRosterResultsInput | null;
    /**
     * The district's brief slot and email switch; null restores the default
     * (06:50 local, no email). The slot is validated server-side.
     */
    brief?: import('../../../common/src/types/brief').BriefSchedule | null;
    /** IANA zone the district's brief is scheduled and keyed by; null clears it. */
    timezone?: string | null;
  }
): Promise<void> => {
  const fn = httpsCallable(functions, 'updateDistrictIntegration');
  await fn({ districtId, ...updates });
};

/** The `/results` settings as the console sends them (§3.2). */
export interface OneRosterResultsInput {
  enabled: boolean;
  /** Regex source; null or blank means the built-in default. */
  line_item_pattern?: string | null;
  /** `'current'` or an `/academicSessions` sourcedId. */
  grading_period?: string | null;
}

/** The policy as the console sends it — dates as ISO strings, blanks as null. */
export interface AcademicPolicyInput {
  enabled: boolean;
  min_gpa?: number | null;
  max_f_count?: number | null;
  scale?: 4 | 5;
  check: 'term' | 'progress';
  term_label?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  note?: string | null;
}

/**
 * The district's academic-eligibility report (AUTOMATION_SPEC A11): every
 * ineligible athlete by school and team, with the numbers behind the verdict.
 * Server-computed so the console never reads a raw roster entry.
 */
export const getAcademicReport = async (
  districtId: string
): Promise<import('../../../common/src/types/district').AcademicReport> => {
  const fn = httpsCallable(functions, 'getAcademicReport');
  const result = await fn({ districtId });
  return result.data as import('../../../common/src/types/district').AcademicReport;
};

export interface DistrictSummary {
  id: string;
  name: string;
  state: string;
  status: string;
  provider_type: string;
  provider_id: string;
  email_domains: string[];
  rostering_source: string;
  sync_enabled: boolean;
  dpa_signed: boolean;
  organization_ids: string[];
}

/** Super-admin: list all districts for the provisioning UI. */
export const listDistricts = async (): Promise<DistrictSummary[]> => {
  const fn = httpsCallable(functions, 'listDistricts');
  const result = await fn({});
  return (result.data as { districts: DistrictSummary[] }).districts;
};

/** Super-admin: create or update a district (server-owned config). */
export const upsertDistrict = async (payload: Record<string, unknown>): Promise<void> => {
  const fn = httpsCallable(functions, 'upsertDistrict');
  await fn(payload);
};

export interface HealthDetailItem {
  sourced_id: string;
  display_name: string;
  grade: string | null;
  school: string | null;
  /** Guardian checks: whether a reach email ever went out (never the address). */
  note?: string;
}

export interface HealthDetailPage {
  items: HealthDetailItem[];
  next_cursor: string | null;
  total: number;
}

/**
 * Page through the people behind one data-health check
 * (DISTRICT_CONSOLE_SPEC §6.3). Field-limited server-side — the console
 * gets enough to act, never the raw roster mirror.
 */
export const getDistrictHealthDetail = async (
  districtId: string,
  checkKey: string,
  cursor?: string,
  school?: string
): Promise<HealthDetailPage> => {
  const fn = httpsCallable(functions, 'getDistrictHealthDetail');
  const result = await fn({
    districtId,
    checkKey,
    ...(cursor ? { cursor } : {}),
    ...(school ? { school } : {}),
  });
  return result.data as HealthDetailPage;
};

export interface GuardianReachCheckResult {
  sent: number;
  remaining: number;
  skipped_recent: number;
}

/**
 * Reach assurance (AUTOMATION_SPEC A3): email a confirmation link to every
 * guardian the district lists who has never been reached. Capped per call;
 * `remaining` says how many are left for the next batch.
 */
export const sendGuardianReachCheck = async (
  districtId: string
): Promise<GuardianReachCheckResult> => {
  const fn = httpsCallable(functions, 'sendGuardianReachCheck');
  const result = await fn({ districtId });
  return result.data as GuardianReachCheckResult;
};

// ===== CALENDAR IN / OUT (AUTOMATION_SPEC §3.4) =====

export type CalendarFeedScope = 'team' | 'organization' | 'me';

export interface CalendarFeedUrl {
  url: string;
  webcal_url: string;
  name: string;
}

/** A subscribable ICS feed for this team, this organization, or everything the caller is on. */
export const getCalendarFeedUrl = async (
  scope: CalendarFeedScope,
  id?: string
): Promise<CalendarFeedUrl> => {
  const fn = httpsCallable(functions, 'getCalendarFeedUrl');
  const result = await fn({ scope, ...(id ? { id } : {}) });
  return result.data as CalendarFeedUrl;
};

export interface ScheduleFeedResult {
  enabled: boolean;
  url?: string;
  created?: number;
  updated?: number;
  cancelled?: number;
  seen?: number;
}

/** Subscribe a team to the schedule feed its district already publishes (null clears it). Pulls at once. */
export const setScheduleFeed = async (
  teamId: string,
  url: string | null,
  timezone?: string
): Promise<ScheduleFeedResult> => {
  const fn = httpsCallable(functions, 'setScheduleFeed');
  const result = await fn({ teamId, url, ...(timezone ? { timezone } : {}) });
  return result.data as ScheduleFeedResult;
};

/* ───────────── Public schedule (AUTOMATION_SPEC A10) ───────────── */

export interface PublicScheduleResult {
  enabled: boolean;
  /** '' when the page has never been published and is not being published now. */
  slug: string;
  url: string;
  embed_url: string;
}

/**
 * Turn a team's or a school's public schedule page on or off. The link is
 * minted on the first enable and kept afterwards, so switching the page off
 * for the summer does not break anything the school printed.
 */
export const setPublicSchedule = async (params: {
  scope: 'team' | 'organization';
  id: string;
  enabled: boolean;
}): Promise<PublicScheduleResult> => {
  const fn = httpsCallable(functions, 'setPublicSchedule');
  const result = await fn(params);
  return result.data as PublicScheduleResult;
};

/* ───────────── Event cascades (AUTOMATION_SPEC A5/A6) ───────────── */

export type LogisticsKind = 'transport' | 'facility';
export type LogisticsRequestOutcome = 'requested' | 'no_contact';

export interface EventLogisticsRequest {
  transport?: {
    headcount?: number;
    depart_at?: Date;
    return_at?: Date;
    destination?: string;
    note?: string;
  };
  facility?: { space?: string; note?: string };
}

export interface EventLogisticsResult {
  transport?: LogisticsRequestOutcome;
  facility?: LogisticsRequestOutcome;
  headcount?: number;
}

/**
 * Draft and email the bus request / facility hold for an event to the
 * organization's contacts (the "human API"). 'no_contact' means the org
 * admin has not set that contact yet; nothing was sent for it.
 */
export const requestEventLogistics = async (
  teamId: string,
  eventId: string,
  request: EventLogisticsRequest
): Promise<EventLogisticsResult> => {
  const fn = httpsCallable(functions, 'requestEventLogistics');
  const payload: Record<string, unknown> = { teamId, eventId };
  if (request.transport) {
    const t = request.transport;
    payload.transport = {
      ...(typeof t.headcount === 'number' ? { headcount: t.headcount } : {}),
      ...(t.depart_at ? { depart_at: t.depart_at.toISOString() } : {}),
      ...(t.return_at ? { return_at: t.return_at.toISOString() } : {}),
      ...(t.destination ? { destination: t.destination } : {}),
      ...(t.note ? { note: t.note } : {}),
    };
  }
  if (request.facility) {
    payload.facility = {
      ...(request.facility.space ? { space: request.facility.space } : {}),
      ...(request.facility.note ? { note: request.facility.note } : {}),
    };
  }
  const result = await fn(payload);
  return result.data as EventLogisticsResult;
};

/** Record the office's reply (confirmed/declined, with their reference) or give the bus/room back. */
export const setEventLogisticsStatus = async (
  teamId: string,
  eventId: string,
  kind: LogisticsKind,
  status: 'confirmed' | 'declined' | 'released',
  reference?: string
): Promise<void> => {
  const fn = httpsCallable(functions, 'setEventLogisticsStatus');
  await fn({ teamId, eventId, kind, status, ...(reference ? { reference } : {}) });
};

/* ── Conflict detection (AUTOMATION_SPEC A9) ── */

export type EventConflictKind = 'venue' | 'coach' | 'team' | 'transport';

/**
 * One clash, as the callable returns it: the other side of it, with the
 * window as ISO strings (the client turns them into Dates to render).
 */
export interface EventConflictItem {
  kind: EventConflictKind;
  severity: 'warning' | 'info';
  team_id: string;
  team_name: string;
  event_id: string;
  title: string;
  start: string;
  end: string;
  location?: string | null;
  /** The shared coach for `coach`, the bus window for `transport`. */
  detail?: string | null;
}

/**
 * What the whole organization already has booked against this window —
 * asked before the event is written, so the coach sees the gym clash while
 * they can still change the time. Writes nothing.
 */
export const checkEventConflicts = async (params: {
  teamId: string;
  /** Omit when the event does not exist yet; supplied, it excludes itself. */
  eventId?: string;
  title?: string;
  type?: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  location?: string;
  transportNeeded?: boolean;
}): Promise<{ conflicts: EventConflictItem[]; timezone: string }> => {
  const fn = httpsCallable(functions, 'checkEventConflicts');
  const result = await fn({
    teamId: params.teamId,
    ...(params.eventId ? { eventId: params.eventId } : {}),
    ...(params.title ? { title: params.title } : {}),
    ...(params.type ? { type: params.type } : {}),
    start: params.start.toISOString(),
    end: params.end.toISOString(),
    ...(params.allDay ? { allDay: true } : {}),
    ...(params.location ? { location: params.location } : {}),
    ...(params.transportNeeded ? { transportNeeded: true } : {}),
  });
  return result.data as { conflicts: EventConflictItem[]; timezone: string };
};

export interface OrganizationScheduleEvent {
  id: string;
  team_id: string;
  team_name: string;
  team_color?: string | null;
  title: string;
  type: string;
  start: string;
  end: string;
  all_day: boolean;
  location: string | null;
  status: string;
  status_reason: string | null;
  opponent: string | null;
  home_away: string | null;
  source: string;
  /** How many conflicts the A9 stamp holds (0 when clean). */
  conflicts?: number;
}

/** Every team's events in a range — the department view. */
export const getOrganizationSchedule = async (
  organizationId: string,
  from?: Date,
  to?: Date
): Promise<{ events: OrganizationScheduleEvent[]; teams: Array<{ id: string; name: string }> }> => {
  const fn = httpsCallable(functions, 'getOrganizationSchedule');
  const result = await fn({
    organizationId,
    ...(from ? { from: from.toISOString() } : {}),
    ...(to ? { to: to.toISOString() } : {}),
  });
  return result.data as { events: OrganizationScheduleEvent[]; teams: Array<{ id: string; name: string }> };
};

// ===== SCHEDULE ANALYSIS HELPERS =====

export interface ScheduleEvent {
  title: string;
  type: 'practice' | 'game' | 'meeting' | 'other';
  start_date: string;  // YYYY-MM-DD
  start_time: string;  // HH:MM
  end_date: string;    // YYYY-MM-DD
  end_time: string;    // HH:MM
  location: string;
  description: string;
  opponent?: string;
}

export interface ScheduleAnalysisResult {
  events: ScheduleEvent[];
  confidence: number; // 0-100
  notes?: string;
}

/**
 * Analyzes a schedule image/PDF using AI Vision
 * Extracts events from team schedules and returns structured data
 * Uses the deployed manageExternalAPIs function
 */
export const analyzeScheduleWithGPT = async (params: {
  imageBase64: string;
  contentType: string;
}): Promise<ScheduleAnalysisResult> => {
  const manageExternalAPIs = httpsCallable(functions, 'manageExternalAPIs');
  const result = await manageExternalAPIs({
    action: 'analyzeScheduleWithGPT',
    ...params,
  });
  return result.data as ScheduleAnalysisResult;
};

// ===== LINEUP DOMAIN HELPERS =====

/**
 * Response from exportLineupPDF
 */
export interface ExportLineupPDFResponse {
  success: boolean;
  downloadUrl?: string;
  filename?: string;
  error?: string;
}

/**
 * Response from sendLineupToTeam
 */
export interface SendLineupToTeamResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Export lineup as PDF
 * Returns a signed URL to download the PDF (valid for 1 hour)
 */
export const exportLineupPDF = async (params: {
  teamId: string;
  lineupId: string;
}): Promise<ExportLineupPDFResponse> => {
  return cloudFunctionManageRequest('manageLineups', {
    function: 'exportLineupPDF',
    ...params,
  });
};

/**
 * Send lineup to team channel as a formatted message
 */
export const sendLineupToTeam = async (params: {
  teamId: string;
  lineupId: string;
  message?: string; // Optional additional message from coach
}): Promise<SendLineupToTeamResponse> => {
  return cloudFunctionManageRequest('manageLineups', {
    function: 'sendLineupToTeam',
    ...params,
  });
};

/* ───────────────────────────── Film room ───────────────────────────── */

export interface FilmRoomStatus {
  enabled: boolean;
  model: string;
  segment_seconds: number;
  month: string;
  minutes_used: number;
  minutes_limit: number;
  client_minutes_used: number;
  client_minutes_limit: number;
}

export interface FilmRoomAdminStatus {
  provider: string;
  emulator: boolean;
  config: {
    enabled: boolean;
    model: string;
    segment_seconds: number;
    monthly_minutes_per_team: number;
    monthly_minutes_per_client: number;
    input_usd_per_million: number;
    output_usd_per_million: number;
    media_resolution: 'default' | 'low';
  };
  month: string;
  usage: { minutes: number; films: number; input_tokens: number; output_tokens: number; cost_estimate_usd: number };
}

/** Ask the film-room job to break a film down (FILM_ROOM_SPEC). Staff only; minutes are reserved on the server. */
export const analyzeFilm = async (params: { teamId: string; filmId: string; shareWithFamilies: boolean; ourLook?: string }) => {
  const fn = httpsCallable(functions, 'analyzeFilm');
  const result = await fn(params);
  return result.data as { status: string; segments_total: number; minutes_charged: number; model: string };
};

export const getFilmRoomStatus = async (teamId: string): Promise<FilmRoomStatus> => {
  const fn = httpsCallable(functions, 'getFilmRoomStatus');
  const result = await fn({ teamId });
  return result.data as FilmRoomStatus;
};

export const askFilm = async (params: { teamId: string; filmId: string; question: string }): Promise<{ id: string; answer: string }> => {
  const fn = httpsCallable(functions, 'askFilm');
  const result = await fn(params);
  return result.data as { id: string; answer: string };
};

export const filmRoomAdmin = async (action: 'status' | 'set', config?: Partial<FilmRoomAdminStatus['config']>): Promise<FilmRoomAdminStatus> => {
  const fn = httpsCallable(functions, 'filmRoomAdmin');
  const result = await fn({ action, config });
  return result.data as FilmRoomAdminStatus;
};

/* ───────────────────────────── Participation report ───────────────────────────── */

export type {
  ParticipationAthlete,
  ParticipationCounts,
  ParticipationCsvView,
  ParticipationEventType,
  ParticipationGender,
  ParticipationReport,
  ParticipationSportRow,
  ParticipationTeamRow,
  ParticipationTotals,
} from '../../../common/src/types/participation';

/**
 * The season's roll call read back out (AUTOMATION_SPEC P3): the
 * sport-by-gender counts the state association asks for, plus the athlete
 * sheet. Org admins (and the district admin over the org) get the whole
 * department; `teamId` is how team staff open their own team.
 */
export const getParticipationReport = async (params: {
  organizationId: string;
  from: Date;
  to: Date;
  teamId?: string;
}): Promise<ParticipationReport> => {
  const fn = httpsCallable(functions, 'getParticipationReport');
  const result = await fn({
    organizationId: params.organizationId,
    from: params.from.toISOString(),
    to: params.to.toISOString(),
    ...(params.teamId ? { teamId: params.teamId } : {}),
  });
  return result.data as ParticipationReport;
};

/* ───────────────────────────── Department brief ───────────────────────────── */

import type { BriefAction, DepartmentBrief } from '../../../common/src/types/brief';

const briefDates = (raw: any): DepartmentBrief => ({
  ...raw,
  generated_at: raw?.generated_at ? new Date(raw.generated_at) : new Date(),
  notified_at: raw?.notified_at ? new Date(raw.notified_at) : null,
  signals: (raw?.signals || []).map((s: any) => ({ ...s, when: s?.when ? new Date(s.when) : null })),
});

/** Today's brief for a school (DEPARTMENT_BRIEF_SPEC); `refresh` regenerates unless one was made in the last ten minutes. */
export const getDepartmentBrief = async (organizationId: string, refresh = false): Promise<{ brief: DepartmentBrief; refreshed: boolean }> => {
  const fn = httpsCallable(functions, 'getDepartmentBrief');
  const result = await fn({ organizationId, refresh });
  const data = result.data as { brief: any; refreshed: boolean };
  return { brief: briefDates(data.brief), refreshed: data.refreshed };
};

/** A proposal's dates arrive as ISO strings; the card compares `expires_at` to the clock. */
export const proposalDates = (raw: any): BriefProposal | null =>
  raw && typeof raw === 'object'
    ? {
        ...raw,
        proposed_at: raw.proposed_at ? new Date(raw.proposed_at) : new Date(),
        expires_at: raw.expires_at ? new Date(raw.expires_at) : new Date(0),
        executed_at: raw.executed_at ? new Date(raw.executed_at) : null,
        snapshot: { event_updated_at: raw.snapshot?.event_updated_at ? new Date(raw.snapshot.event_updated_at) : null, attendance_pending: raw.snapshot?.attendance_pending ?? null },
      }
    : null;

export interface AskAnswer {
  id: string;
  answer: string;
  links: BriefAction[];
  tools_used: string[];
  /** What the assistant proposes to do, for the person to confirm (NEXT_QUEUE_SPEC §5). */
  proposal: BriefProposal | null;
}

export const askDepartment = async (organizationId: string, question: string): Promise<AskAnswer> => {
  const fn = httpsCallable(functions, 'askDepartment');
  const result = await fn({ organizationId, question });
  const data = result.data as any;
  return { ...data, proposal: proposalDates(data?.proposal) };
};

/** Confirm a proposal the assistant made on a question; idempotent. */
export const confirmBriefAction = async (scope: { organizationId?: string; districtId?: string }, questionId: string): Promise<BriefProposalResult & { proposal: BriefProposal | null }> => {
  const fn = httpsCallable(functions, 'confirmBriefAction');
  const result = await fn({ ...scope, questionId });
  const data = result.data as any;
  return { ok: !!data?.ok, message: String(data?.message || ''), links: Array.isArray(data?.links) ? data.links : [], proposal: proposalDates(data?.proposal) };
};

/* ───────────────────────────── District brief ───────────────────────────── */

import type { BriefProposal, BriefProposalResult, DistrictBrief } from '../../../common/src/types/brief';

const districtBriefDates = (raw: any): DistrictBrief => ({
  ...raw,
  generated_at: raw?.generated_at ? new Date(raw.generated_at) : new Date(),
  notified_at: raw?.notified_at ? new Date(raw.notified_at) : null,
  schools: (raw?.schools || []).map((s: any) => ({
    ...s,
    generated_at: s?.generated_at ? new Date(s.generated_at) : new Date(),
    act: (s?.act || []).map((x: any) => ({ ...x, when: x?.when ? new Date(x.when) : null })),
    watch: (s?.watch || []).map((x: any) => ({ ...x, when: x?.when ? new Date(x.when) : null })),
  })),
});

/** The district's roll-up of every school's brief (DEPARTMENT_BRIEF_SPEC §10). */
export const getDistrictBrief = async (districtId: string, refresh = false): Promise<{ brief: DistrictBrief; refreshed: boolean }> => {
  const fn = httpsCallable(functions, 'getDistrictBrief');
  const result = await fn({ districtId, refresh });
  const data = result.data as { brief: any; refreshed: boolean };
  return { brief: districtBriefDates(data.brief), refreshed: data.refreshed };
};

export const askDistrict = async (districtId: string, question: string): Promise<AskAnswer> => {
  const fn = httpsCallable(functions, 'askDistrict');
  const result = await fn({ districtId, question });
  const data = result.data as any;
  return { ...data, proposal: proposalDates(data?.proposal) };
};
